import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Alert,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { History as HistoryIcon, Download as DownloadIcon } from '@mui/icons-material';
import { activityLogService, ActivityLog, ActivityType } from '../services/activityLogService';
import { formatDateTime } from '../utils/dateUtils';
import RequirePermission from '../components/common/RequirePermission';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { activityLogColumns } from '../config/activityLogColumns';

const activityTypeOptions: { value: ActivityType | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'login_success', label: 'Login Success' },
  { value: 'login_failed', label: 'Login Failed' },
  { value: 'logout', label: 'Logout' },
  { value: 'patient_created', label: 'Patient Created' },
  { value: 'patient_updated', label: 'Patient Updated' },
  { value: 'patient_finished', label: 'Patient Finished' },
  { value: 'patient_revived', label: 'Patient Revived' },
  { value: 'meeting_created', label: 'Meeting Created' },
  { value: 'meeting_updated', label: 'Meeting Updated' },
  { value: 'meeting_status_changed', label: 'Meeting Status Changed' },
  { value: 'meeting_text_created', label: 'Meeting Text Created' },
  { value: 'meeting_text_updated', label: 'Meeting Text Updated' },
  { value: 'meeting_text_deleted', label: 'Meeting Text Deleted' },
];

const getActivityTypeColor = (type: ActivityType): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (type === 'login_success') return 'success';
  if (type === 'login_failed') return 'error';
  if (type.includes('created')) return 'success';
  if (type.includes('updated')) return 'info';
  if (type.includes('deleted')) return 'error';
  if (type.includes('finished')) return 'warning';
  return 'default';
};

const getActivityTypeLabel = (type: ActivityType): string => {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ActivityLogsPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const userTimezone = useUserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Server-side pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  // Server-side activity type filter
  const [activityType, setActivityType] = useState<ActivityType | ''>('');

  // Entity-specific callbacks for useTablePage
  const getSortValue = useCallback((log: ActivityLog, key: string): any => {
    switch (key) {
      case 'dateTime': return log.created_at;
      case 'user': return (log.user_name || '').toLowerCase();
      case 'activityType': return log.activity_type;
      case 'description': return (log.description || '').toLowerCase();
      case 'patient': return (log.patient_name || '').toLowerCase();
      case 'meeting': return (log.meeting_title || '').toLowerCase();
      default: return '';
    }
  }, []);

  // Search is server-side, but we provide text fields so client-side filtering
  // in sortedData doesn't incorrectly remove results
  const getSearchText = useCallback((log: ActivityLog): string[] => {
    return [
      log.user_name || '',
      log.description || '',
      log.patient_name || '',
      log.meeting_title || '',
      log.activity_type || '',
    ];
  }, []);

  const table = useTablePage<ActivityLog>({
    entityType: 'activityLogs',
    columns: activityLogColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  // Fetch logs from server (server-side pagination, search, and activity type filter)
  useEffect(() => {
    const fetchLogs = async () => {
      table.setLoading(true);
      table.setError(null);
      try {
        const response = await activityLogService.getActivityLogs({
          page: page + 1,
          page_size: rowsPerPage,
          search: table.searchQuery || undefined,
          activity_type: activityType || undefined,
          sort_by: 'created_at',
          sort_order: 'desc',
        });
        table.setData(response.items);
        setTotal(response.total);
      } catch (err: unknown) {
        const error = err as Error;
        table.setError(error.message || 'Failed to fetch activity logs');
      } finally {
        table.setLoading(false);
      }
    };

    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, table.searchQuery, activityType]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [table.searchQuery, activityType]);

  const SUMMARY_TYPES: ActivityType[] = ['MEETING_SUMMARY_RUN', 'QUESTIONNAIRE_SUMMARY_RUN'];
  const canDownloadPrompt = !!(user?.user_privilege?.prompt_development);

  const handleDownloadPrompt = async (log: ActivityLog) => {
    try {
      const url = await activityLogService.downloadPromptFile(log.id);
      window.open(url, '_blank');
    } catch {
      // silently ignore — backend returns 404 if no file
    }
  };

  // Column value renderer
  const getColumnValue = (log: ActivityLog, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'dateTime': return formatDateTime(log.created_at, {}, userTimezone);
      case 'user': return log.user_name || `User #${log.user_id}`;
      case 'activityType':
        return (
          <Chip
            label={getActivityTypeLabel(log.activity_type)}
            color={getActivityTypeColor(log.activity_type)}
            size="small"
          />
        );
      case 'description': return log.description;
      case 'patient': return log.patient_name || (log.patient_id ? `Patient #${log.patient_id}` : '-');
      case 'meeting': return log.meeting_title || (log.meeting_id ? `Meeting #${log.meeting_id}` : '-');
      case 'actions':
        if (canDownloadPrompt && SUMMARY_TYPES.includes(log.activity_type)) {
          return (
            <Tooltip title="Download prompt & result">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownloadPrompt(log); }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }
        return null;
      default: return '';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (log: ActivityLog) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(log.created_at, {}, userTimezone)}
        </Typography>
        <Chip
          label={getActivityTypeLabel(log.activity_type)}
          color={getActivityTypeColor(log.activity_type)}
          size="small"
        />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {log.user_name || `User #${log.user_id}`}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {log.description}
      </Typography>
      {log.patient_name && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Patient: {log.patient_name}
        </Typography>
      )}
      {log.meeting_title && (
        <Typography variant="body2" color="text.secondary">
          Meeting: {log.meeting_title}
        </Typography>
      )}
    </>
  );

  return (
  <RequirePermission check={!!user?.user_privilege?.config} featureName="activity logs">
    <TablePageLayout loading={false} error={null}>
      <DataTableToolbar
        {...table.toolbarProps}
        title="Activity Logs"
        searchPlaceholder="Search activity logs..."
        isAdmin={!!user?.user_privilege?.config}
        actions={
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={activityType}
              label="Activity Type"
              onChange={(e) => {
                setActivityType(e.target.value as ActivityType | '');
                setPage(0);
              }}
            >
              {activityTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />

      {table.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {table.error}
        </Alert>
      )}

      {table.loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      ) : isMobile ? (
        <>
          <MobileCardGrid
            data={table.data}
            renderCard={renderMobileCard}
            getItemKey={(log) => log.id}
            emptyIcon={<HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No activity logs found"
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.data}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(log) => log.id}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </DataTableContainer>
      )}

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!user?.user_privilege?.config} />
    </TablePageLayout>
  </RequirePermission>
  );
};

export default ActivityLogsPage;
